import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  ViewChild,
  inject
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FieldFilter, SeasonMapDto, SeasonService } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { OpenStreetMapComponent } from '@app/components/openstreetmap/openstreetmap.component';
import { MapControllerInput, MapInitOptions } from '@app/components/openstreetmap/interfaces';
import { INDEX_LEGENDS, LegendItem } from '@app/components/openstreetmap/legend-configs';
import { IndexManager } from '@app/components/openstreetmap/managers/index.manager';
import { OSM_RASTER_TILE_LAYER_OPTIONS } from '@app/components/openstreetmap/constants/osm-tile-layer-performance.util';
import { TilesManager } from '@app/pages/weather/pages/gis-weather/managers/tiles.manager';
import { WeatherLegendComponent } from '@app/pages/weather/pages/gis-weather/components/weather-legend/weather-legend.component';
import { FilterOperator } from '@app/utils/filter-operator';
import { CropCacheService } from '@services/caches/crop-cache.service';
import { IndexType } from '@services/satellite-indices.service';
import {
  isExcludedFromWeatherDataType,
  isYearlyWeatherLayerName
} from '@app/pages/weather/pages/gis-weather/utils/weather-layer-name.util';
import { environment } from '@root/src/environments/environment';
import { ChatDatasetArtifact, ChatMapArtifact } from '../models/orchestrator-chat.models';
import {
  AnalyticsAnomalyLegendItem,
  AnalyticsMapLegend,
  AnalyticsMapPoint,
  AnalyticsMapPolygon,
  DENSITY_METRIC,
  FACT_SEASON_COLOR,
  INDEX_TYPE_OPTIONS,
  PLAN_SEASON_COLOR,
  RasterRequest,
  areaSourceForDensity,
  buildAnalyticsMapScene,
  canOfferDensity,
  catalogDaysInWindow,
  datasetCropId,
  datasetPeriod,
  defaultCatalogDay,
  defaultMetric,
  formatIsoDay,
  indexTileUrl,
  isAreaMetric,
  isFieldScope,
  isIndexLayerName,
  latLngBoundsBox,
  wktToLeafletPolygons,
  metricLabel,
  numericColumns,
  rasterRequest,
  recentCatalogDays,
  recentYears,
  rowIsoDays,
  sceneKey,
  seasonLayerFilters,
  weatherLayerNames,
  weatherTileUrl,
  weatherTokenFor,
  wmsTileUrl,
  yearsCovering
} from './analytics-map.logic';

/** Leaflet map surface the card uses. Typed loosely because the host component exposes `any`. */
interface LeafletMapLike {
  fitBounds: (bounds: unknown, options?: unknown) => void;
  invalidateSize: (animate?: boolean) => void;
  getZoom: () => number;
  getSize: () => { x: number; y: number };
  getPane: (name: string) => HTMLElement | undefined;
  createPane: (name: string) => HTMLElement;
  removeLayer: (layer: unknown) => void;
  hasLayer: (layer: unknown) => boolean;
}

interface DrawnSeason {
  id: string;
  name: string;
  coordinates: [number, number][];
  wktCoordinates: [number, number][][][];
  popup: string;
  fill: string;
}

interface LeafletTileLayerLike {
  addTo: (map: unknown) => unknown;
  on: (event: string, handler: (event?: unknown) => void) => void;
  off?: (event?: string) => void;
  bringToFront?: () => void;
}

interface LeafletLayerGroupLike {
  addTo: (map: unknown) => unknown;
  clearLayers: () => void;
}

interface LeafletLike {
  latLngBounds: (points: [number, number][]) => unknown;
  layerGroup: (layers: unknown[], options?: unknown) => LeafletLayerGroupLike;
  circleMarker: (latlng: [number, number], options?: unknown) => {
    addTo: (target: unknown) => unknown;
    bindTooltip: (html: string, options?: unknown) => unknown;
    bindPopup: (html: string) => unknown;
  };
  TileLayer: { extend: (props: Record<string, unknown>) => new (url: string, options?: unknown) => unknown };
}

/** Raster the user can put under the vector layers. */
export type AnalyticsRasterMode = 'none' | 'index' | 'weather';

const RASTER_PANE = 'analyticsRasterPane';
const RASTER_PANE_Z_INDEX = '450';
const POINT_PANE = 'analyticsPointPane';
const POINT_PANE_Z_INDEX = '910';
const ANOMALY_PANE = 'analyticsAnomalyPane';
const ANOMALY_PANE_Z_INDEX = '905';
const DISTRICT_PANE = 'analyticsDistrictPane';
const DISTRICT_PANE_Z_INDEX = '480';
const PLAN_PANE = 'analyticsPlanPane';
const PLAN_PANE_Z_INDEX = '620';
const FACT_PANE = 'analyticsFactPane';
const FACT_PANE_Z_INDEX = '640';
const FIT_MAX_ZOOM = 16;
const NO_TILES_NOTE = 'Нет тайлов за выбранный период.';
const TILES_FAILED_NOTE = 'Тайлы слоя не загрузились для этого масштаба.';
const RASTER_MODE_LABELS: Record<AnalyticsRasterMode, string> = {
  none: 'Нет',
  index: 'Индекс',
  weather: 'Погода'
};

@Component({
  selector: 'app-analytics-map-card',
  standalone: true,
  imports: [CommonModule, OpenStreetMapComponent, TranslateModule, WeatherLegendComponent],
  template: `
    <div class="analytics-map">
      <div class="map-toolbar">
        <div class="picker">
          <span>Подложка</span>
          <button type="button" class="picker-value" (click)="toggleMenu('base', $event)">
            {{ basemap === 'google' ? 'Google' : 'OpenStreetMap' }}
          </button>
          <ul class="picker-menu" *ngIf="openMenu === 'base'">
            <li>
              <button type="button" [class.active]="basemap === 'plan'" (click)="chooseBasemap('plan', $event)">
                OpenStreetMap
              </button>
            </li>
            <li>
              <button type="button" [class.active]="basemap === 'google'" (click)="chooseBasemap('google', $event)">
                Google
              </button>
            </li>
          </ul>
        </div>
        <div class="picker" *ngIf="rasterModes.length > 1">
          <span>Слой</span>
          <button type="button" class="picker-value" (click)="toggleMenu('mode', $event)">
            {{ rasterModeLabel(rasterMode) }}
          </button>
          <ul class="picker-menu" *ngIf="openMenu === 'mode'">
            <li *ngFor="let mode of rasterModes">
              <button type="button" [class.active]="mode === rasterMode" (click)="chooseMode(mode, $event)">
                {{ rasterModeLabel(mode) }}
              </button>
            </li>
          </ul>
        </div>
        <div class="picker" *ngIf="rasterMode !== 'none' && dateOptions.length > 0">
          <span>Дата</span>
          <button type="button" class="picker-value" (click)="toggleMenu('date', $event)">
            {{ formatIsoDay(selectedDay) }}
          </button>
          <ul class="picker-menu" *ngIf="openMenu === 'date'">
            <li *ngFor="let day of dateOptions">
              <button type="button" [class.active]="day === selectedDay" (click)="chooseDay(day, $event)">
                {{ formatIsoDay(day) }}
              </button>
            </li>
          </ul>
        </div>
        <div class="picker" *ngIf="rasterMode === 'index' && indexOptions.length > 1">
          <span>Индекс</span>
          <button type="button" class="picker-value" (click)="toggleMenu('index', $event)">
            {{ selectedIndexType }}
          </button>
          <ul class="picker-menu" *ngIf="openMenu === 'index'">
            <li *ngFor="let indexType of indexOptions">
              <button type="button" [class.active]="indexType === selectedIndexType" (click)="chooseIndex(indexType, $event)">
                {{ indexType }}
              </button>
            </li>
          </ul>
        </div>
        <div class="picker" *ngIf="metricOptions.length > 1">
          <span>Показатель</span>
          <button type="button" class="picker-value" (click)="toggleMenu('metric', $event)">
            {{ selectedMetric ? metricLabel(selectedMetric) : '' }}
          </button>
          <ul class="picker-menu" *ngIf="openMenu === 'metric'">
            <li *ngFor="let metric of metricOptions">
              <button type="button" [class.active]="metric === selectedMetric" (click)="chooseMetric(metric, $event)">
                {{ metricLabel(metric) }}
              </button>
            </li>
          </ul>
        </div>
        <label class="layer-toggle" *ngIf="anomalyLegend.length">
          <input type="checkbox" [checked]="anomaliesVisible" (change)="toggleAnomalies($event)" />
          <span>Аномалии</span>
        </label>
        <label class="layer-toggle" *ngIf="legend">
          <input type="checkbox" [checked]="districtFillVisible" (change)="toggleDistrictFill($event)" />
          <span>Заливка районов</span>
        </label>
        <div class="legend metric-legend" *ngIf="legend && districtFillVisible">
          <span class="legend-name">{{ legend.label }}</span>
          <span class="legend-hint">{{ legend.hint }}</span>
          <span class="ramp-row">
            <span>{{ legend.min | number:'1.0-0' }}</span>
            <span class="ramp"></span>
            <span>{{ legend.max | number:'1.0-0' }}</span>
          </span>
        </div>
        <p class="map-warning" *ngIf="districtsClickable && !seasonsLoaded && !seasonsLoading">
          Нажмите на район, чтобы показать плановые и фактические посевы.
        </p>
        <ng-container *ngIf="seasonsLoaded">
          <label class="layer-toggle">
            <input type="checkbox" [checked]="planSeasonsVisible" (change)="toggleSeasonLayer('plan', $event)" />
            <i class="swatch plan-swatch"></i><span>Плановые посевы</span>
          </label>
          <label class="layer-toggle">
            <input type="checkbox" [checked]="factSeasonsVisible" (change)="toggleSeasonLayer('fact', $event)" />
            <i class="swatch fact-swatch"></i><span>Фактические посевы</span>
          </label>
        </ng-container>
        <div class="legend index-legend" *ngIf="showIndexLegend">
          <span class="anomaly-item" *ngFor="let item of indexLegendItems">
            <i class="swatch index-swatch" [style.background]="item.color"></i>
            <span class="index-range">{{ item.range }}</span>
            {{ item.descriptionKey | translate }}
          </span>
        </div>
        <app-weather-legend
          *ngIf="showWeatherLegend"
          [embedded]="true"
          [visible]="true"
          [selectedWeatherLayer]="weatherLayerName"></app-weather-legend>
        <div class="legend anomaly-legend" *ngIf="anomaliesVisible && anomalyLegend.length">
          <span class="anomaly-item" *ngFor="let item of anomalyLegend">
            <i class="swatch" [style.background]="item.color"></i>{{ item.type }}
          </span>
        </div>
        <p class="map-warning" *ngIf="rasterNote">{{ rasterNote }}</p>
        <p class="map-warning" *ngIf="seasonsLoading">Загрузка посевов…</p>
        <p class="map-warning" *ngIf="seasonNote">{{ seasonNote }}</p>
        <p class="map-warning" *ngFor="let warning of warnings">{{ warning }}</p>
      </div>
      <div class="map-canvas">
        <app-openstreetmap
          [mapOptions]="mapOptions"
          [mapController$]="mapController"
          (mapReady)="onMapReady($event)"></app-openstreetmap>
        <div class="zoom-readout">{{ zoomLabel }}</div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex: 1 1 auto;
      min-height: 0;
      height: 100%;
    }

    .analytics-map {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
      height: 100%;
      overflow: hidden;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #fff;
    }

    .map-toolbar {
      position: relative;
      z-index: 5;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 12px;
      padding: 8px 10px;
      border-bottom: 1px solid #e5e7eb;
      font-size: 12px;
    }

    .picker {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .picker-value,
    .picker-menu button {
      border: 1px solid #dee0e3;
      border-radius: 8px;
      background: #fff;
      color: #14151a;
      padding: 2px 8px;
      cursor: pointer;
      text-align: left;
    }

    .picker-menu {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      z-index: 6;
      min-width: 180px;
      max-height: 240px;
      margin: 0;
      padding: 4px;
      overflow: auto;
      list-style: none;
      border: 1px solid #dee0e3;
      border-radius: 8px;
      background: #fff;
      box-shadow: 0 8px 24px rgba(20, 21, 26, 0.16);
    }

    .picker-menu button {
      display: block;
      width: 100%;
      border-color: transparent;
    }

    .picker-menu button:hover,
    .picker-menu button.active {
      background: #f2faef;
    }

    .layer-toggle {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
    }

    .legend {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .anomaly-legend {
      flex-wrap: wrap;
    }

    .anomaly-item {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .swatch {
      display: inline-block;
      width: 10px;
      height: 10px;
      border: 1px solid #8a1c1c;
      border-radius: 2px;
    }

    .metric-legend {
      display: flex;
      flex: 1 0 220px;
      flex-direction: column;
      align-items: stretch;
      gap: 4px;
      max-width: 360px;
    }

    .legend-hint {
      color: #5c6370;
      line-height: 1.35;
    }

    .ramp-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .ramp {
      flex: 1 1 auto;
      width: auto;
      min-width: 96px;
      height: 10px;
      border-radius: 999px;
      background: linear-gradient(90deg, #e8f5e4, #145214);
    }

    .index-legend {
      flex-wrap: wrap;
    }

    .metric-swatch {
      border-color: #145214;
    }

    .plan-swatch {
      border-color: #15803d;
      background: #15803d;
    }

    .fact-swatch {
      border-color: #4287f5;
      background: #4287f5;
    }

    .index-swatch {
      border-color: #8a909c;
    }

    .index-range {
      margin-right: 4px;
    }

    app-weather-legend {
      flex: 1 0 100%;
      display: block;
    }

    .map-warning {
      margin: 0;
      color: #8a909c;
    }

    /*
     * Own stacking context: Leaflet panes (z-index 400..1000) stay inside the canvas,
     * so the toolbar menus above are not covered by the map.
     */
    .map-canvas {
      position: relative;
      z-index: 0;
      flex: 1 1 auto;
      min-height: 0;
      height: 100%;
    }

    .map-canvas app-openstreetmap {
      display: block;
      width: 100%;
      height: 100%;
    }

    .zoom-readout {
      position: absolute;
      z-index: 1000;
      top: 78px;
      right: 10px;
      min-width: 30px;
      padding: 0 4px;
      border: 2px solid rgba(0, 0, 0, 0.2);
      border-radius: 4px;
      background: #fff;
      color: #333;
      font: 12px/22px Arial, sans-serif;
      text-align: center;
      pointer-events: none;
    }

    :host ::ng-deep .openstreetmap-container {
      width: 100%;
      height: 100%;
      border: 0;
      border-radius: 0;
    }

    :host-context(body.dark-theme) .analytics-map {
      background: #1f2228;
      border-color: #373839;
      color: #f2f2f7;
    }

    :host-context(body.dark-theme) .map-toolbar {
      border-bottom-color: #373839;
    }

    :host-context(body.dark-theme) .picker-value,
    :host-context(body.dark-theme) .picker-menu {
      background: #2a2d33;
      border-color: #55575c;
      color: #f2f2f7;
    }

    :host-context(body.dark-theme) .picker-menu button {
      background: transparent;
      color: #f2f2f7;
    }

    :host-context(body.dark-theme) .picker-menu button:hover,
    :host-context(body.dark-theme) .picker-menu button.active {
      background: #315f2d;
    }

    :host-context(body.dark-theme) .zoom-readout {
      background: #2a2d33;
      color: #f2f2f7;
      border-color: #55575c;
    }
  `]
})
export class AnalyticsMapCardComponent implements OnChanges, AfterViewInit, OnDestroy {
  private readonly tilesManager = inject(TilesManager);
  private readonly indexManager = inject(IndexManager);
  private readonly seasonService = inject(SeasonService);
  private readonly cropCache = inject(CropCacheService);
  private readonly translate = inject(TranslateService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private catalogSub: Subscription | null = null;
  private layerSub: Subscription | null = null;
  private mapEventsSub: Subscription | null = null;
  private seasonSub: Subscription | null = null;
  private langSub: Subscription | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private zoomControl: { remove?: () => void; _zoomInButton?: HTMLElement; _zoomOutButton?: HTMLElement } | null = null;

  @ViewChild(OpenStreetMapComponent) private osm?: OpenStreetMapComponent;

  @Input() maps: ChatMapArtifact[] = [];
  @Input() dataset: ChatDatasetArtifact | null = null;

  readonly mapController = new Subject<MapControllerInput>();
  readonly mapOptions: MapInitOptions = {
    center: [41.3, 64.6],
    zoom: 6,
    height: '100%',
    showControls: true,
    minimalControls: true,
    drawingMode: false,
    showDrawingControls: false,
    baseMap: 'osm',
    showRegionNames: false,
    disableBounds: true,
    indexOpacityToggle: false,
    hideLayerSwitcher: true
  };

  metricOptions: string[] = [];
  selectedMetric: string | null = null;
  dateOptions: string[] = [];
  selectedDay = '';
  indexOptions: string[] = [];
  selectedIndexType = '';
  rasterModes: AnalyticsRasterMode[] = ['none'];
  rasterMode: AnalyticsRasterMode = 'none';
  rasterNote = '';
  openMenu: 'mode' | 'date' | 'index' | 'metric' | 'base' | null = null;
  basemap: 'plan' | 'google' = 'plan';
  legend: AnalyticsMapLegend | null = null;
  anomalyLegend: AnalyticsAnomalyLegendItem[] = [];
  anomaliesVisible = true;
  /** Choropleth of districts. Off hides both the fill and its legend. */
  districtFillVisible = true;
  warnings: string[] = [];
  zoomLabel = String(Math.round(6));
  weatherLayerName: string | null = null;
  indexRasterOn = false;
  seasonsLoaded = false;
  districtsClickable = false;
  seasonsLoading = false;
  planSeasonsVisible = true;
  factSeasonsVisible = true;
  seasonNote = '';
  readonly formatIsoDay = formatIsoDay;
  readonly metricLabel = metricLabel;

  private mapInstance: LeafletMapLike | null = null;
  private ready = false;
  private polygonIds: string[] = [];
  private anomalyPolygons: AnalyticsMapPolygon[] = [];
  private anomalyIds: string[] = [];
  private bounds: [number, number][] = [];
  private drawnKey = '';
  private rasterKey = '';
  /** The user picked a raster mode by hand; a new answer may still reset it. */
  private modeChosenFor = '';
  /** fitBounds was requested while the map had no size (hidden tab); run it once the canvas is visible. */
  private pendingFit = false;
  /** Raster overlays owned by this card, keyed by id: the dataset raster plus WMS descriptors. */
  private readonly rasterLayers = new Map<string, LeafletTileLayerLike>();
  private pointsLayer: LeafletLayerGroupLike | null = null;
  private readonly rasterLayerId = 'analytics-raster';
  private readonly areasById = new Map<string, AnalyticsMapPolygon>();
  private planSeasons: DrawnSeason[] = [];
  private factSeasons: DrawnSeason[] = [];
  private planSeasonIds: string[] = [];
  private factSeasonIds: string[] = [];
  private seasonScope = '';
  private densitySource: string | null = null;
  private mapEventsBound = false;
  /** The user panned or zoomed; later redraws must not pull the view back to the oblast. */
  private userAdjustedView = false;
  /** Scene already fitted once. A new answer clears this. */
  private fittedScope = '';
  /** True while this card is fitting bounds, so that zoom does not count as a user move. */
  private fitting = false;

  get toolbarVisible(): boolean {
    return this.rasterModes.length > 1
      || this.metricOptions.length > 1
      || (this.rasterMode !== 'none' && this.dateOptions.length > 0)
      || !!this.legend
      || this.anomalyLegend.length > 0
      || this.warnings.length > 0
      || !!this.rasterNote
      || !!this.seasonNote
      || this.seasonsLoading
      || this.seasonsLoaded
      || this.districtsClickable
      || this.showIndexLegend
      || this.showWeatherLegend;
  }

  get showIndexLegend(): boolean {
    return this.rasterMode === 'index' && this.indexRasterOn && this.indexLegendItems.length > 0;
  }

  get showWeatherLegend(): boolean {
    return this.rasterMode === 'weather' && !!this.weatherLayerName;
  }

  get indexLegendItems(): LegendItem[] {
    return INDEX_LEGENDS[this.selectedIndexType] ?? [];
  }

  rasterModeLabel(mode: AnalyticsRasterMode): string {
    return RASTER_MODE_LABELS[mode];
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.pendingFit || this.fittedScope === this.scopeKey() || !this.hasSize()) {
          return;
        }
        this.zone.run(() => this.refit());
      });
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.catalogSub?.unsubscribe();
    this.layerSub?.unsubscribe();
    this.mapEventsSub?.unsubscribe();
    this.seasonSub?.unsubscribe();
    this.langSub?.unsubscribe();
    this.zoomControl?.remove?.();
    this.resizeObserver?.disconnect();
    this.clearRaster();
    for (const id of [...this.rasterLayers.keys()]) {
      this.removeRaster(id);
    }
    this.pointsLayer?.clearLayers();
  }

  ngOnChanges(): void {
    const scope = this.scopeKey();
    if (scope !== this.seasonScope) {
      this.seasonScope = scope;
      this.userAdjustedView = false;
      this.fittedScope = '';
      this.clearSeasonLayers();
    }
    this.refreshMetrics();
    this.refreshRasterModes();
    this.refreshIndexType();
    if (this.ready && this.viewKey() !== this.drawnKey) {
      this.draw();
    }
    this.syncRaster();
  }

  onMapReady(map: unknown): void {
    this.mapInstance = map as LeafletMapLike;
    this.ensurePanes();
    this.addZoomControl(this.mapInstance);
    this.zoomLabel = String(Math.round(this.mapInstance.getZoom()));
    this.bindMapEvents();
    if (!this.mapEventsBound) {
      setTimeout(() => this.bindMapEvents());
    }
    this.ready = true;
    this.draw();
    this.syncRaster();
    this.invalidate();
  }

  toggleSeasonLayer(kind: 'plan' | 'fact', event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (kind === 'plan') {
      this.planSeasonsVisible = checked;
    } else {
      this.factSeasonsVisible = checked;
    }
    this.drawSeasonLayer(kind);
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.openMenu = null;
  }

  toggleMenu(menu: 'mode' | 'date' | 'index' | 'metric' | 'base', event: Event): void {
    event.stopPropagation();
    this.openMenu = this.openMenu === menu ? null : menu;
  }

  chooseMode(mode: AnalyticsRasterMode, event: Event): void {
    event.stopPropagation();
    this.openMenu = null;
    if (mode === this.rasterMode) {
      return;
    }
    this.rasterMode = mode;
    this.modeChosenFor = this.scopeKey();
    this.refreshIndexType();
    this.rasterKey = '';
    this.selectedDay = '';
    this.syncRaster();
  }

  chooseDay(day: string, event: Event): void {
    event.stopPropagation();
    this.openMenu = null;
    if (!day || day === this.selectedDay) {
      return;
    }
    this.selectedDay = day;
    this.applyRaster();
  }

  chooseIndex(indexType: string, event: Event): void {
    event.stopPropagation();
    this.openMenu = null;
    if (!indexType || indexType === this.selectedIndexType) {
      return;
    }
    this.selectedIndexType = indexType;
    this.rasterKey = '';
    this.selectedDay = '';
    this.syncRaster();
  }

  chooseBasemap(layer: 'plan' | 'google', event: Event): void {
    event.stopPropagation();
    this.openMenu = null;
    if (layer === this.basemap) {
      return;
    }
    this.basemap = layer;
    this.osm?.switchBaseMapLayer(layer);
  }

  chooseMetric(metric: string, event: Event): void {
    event.stopPropagation();
    this.openMenu = null;
    this.selectMetric(metric);
  }

  selectMetric(metric: string): void {
    if (!metric || metric === this.selectedMetric) {
      return;
    }
    if (isAreaMetric(metric)) {
      this.densitySource = metric;
    }
    this.selectedMetric = metric;
    if (this.ready) {
      this.draw();
    }
    this.rasterKey = '';
    this.selectedDay = '';
    this.syncRaster();
  }

  toggleDistrictFill(event: Event): void {
    this.districtFillVisible = (event.target as HTMLInputElement).checked;
    if (this.ready) {
      this.draw();
    }
  }

  toggleAnomalies(event: Event): void {
    this.anomaliesVisible = (event.target as HTMLInputElement).checked;
    this.drawAnomalies();
  }

  /** Recomputes the Leaflet size and re-applies the pending fit. Call after the card becomes visible. */
  invalidate(): void {
    this.mapController.next({ invalidateSize: true });
  }

  /** Fits the drawn geometry again, e.g. after the results tab switched to the map. */
  refit(): void {
    if (!this.mapInstance) {
      return;
    }
    try {
      this.mapInstance.invalidateSize(false);
    } catch {
      /* map may be mid-teardown */
    }
    this.fit();
  }

  private refreshMetrics(): void {
    const rows = [
      ...(this.dataset?.map_rows ?? []),
      ...(this.dataset?.preview_rows ?? []),
      ...(this.maps ?? []).flatMap((map) => (map.kind === 'features' ? map.features ?? [] : []))
    ];
    const columns = [
      ...(this.dataset?.columns ?? []),
      ...rows.flatMap((row) => Object.keys(row))
    ];
    const base = numericColumns(rows, [...new Set(columns)]);
    this.densitySource = areaSourceForDensity(base, this.densitySource ?? this.selectedMetric);
    const options = canOfferDensity(this.maps, base) ? [...base, DENSITY_METRIC] : base;
    this.metricOptions = options;
    if (!this.selectedMetric || !options.includes(this.selectedMetric)) {
      this.selectedMetric = defaultMetric(base);
    }
  }

  /**
   * Rasters the answer can carry: what the table columns imply (index or weather series),
   * plus the index layer whenever the answer is about concrete fields or districts.
   */
  private refreshRasterModes(): void {
    const inferred = rasterRequest(this.dataset, this.selectedMetric);
    const modes: AnalyticsRasterMode[] = ['none'];
    const fieldScope = isFieldScope(this.maps, this.dataset);
    if (inferred?.kind === 'index' || fieldScope || this.hasVectorGeography()) {
      modes.push('index');
    }
    if (inferred?.kind === 'weather' || this.weatherColumns().length > 0) {
      modes.push('weather');
    }
    this.rasterModes = modes;

    const scope = this.scopeKey();
    const keepChoice = this.modeChosenFor === scope && modes.includes(this.rasterMode);
    if (keepChoice) {
      return;
    }
    this.modeChosenFor = '';
    if (inferred) {
      this.rasterMode = inferred.kind;
    } else if (fieldScope && modes.includes('index')) {
      // A field answer opens with the latest index mosaic under the contour.
      this.rasterMode = 'index';
    } else {
      this.rasterMode = 'none';
    }
  }

  private draw(): void {
    this.drawnKey = this.viewKey();
    this.ensurePanes();
    const scene = buildAnalyticsMapScene(
      this.maps,
      this.dataset,
      this.selectedMetric,
      this.selectedMetric === DENSITY_METRIC ? this.densitySource : null
    );
    this.legend = scene.legend;
    this.anomalyLegend = scene.anomalyLegend;
    this.warnings = scene.warnings;
    if (this.polygonIds.length > 0) {
      this.mapController.next({ removePolygons: [...this.polygonIds] });
    }
    for (const id of [...this.rasterLayers.keys()]) {
      if (id !== this.rasterLayerId) {
        this.removeRaster(id);
      }
    }

    const wms = scene.wms.filter((layer) => !this.isScrapedRasterName(layer.layerName));
    const areas = scene.polygons.filter((polygon) => polygon.kind === 'area');
    this.anomalyPolygons = scene.polygons.filter((polygon) => polygon.kind === 'anomaly');
    this.polygonIds = areas.map((polygon) => polygon.id);
    this.areasById.clear();
    for (const polygon of areas) {
      this.areasById.set(polygon.id, polygon);
    }
    this.districtsClickable = areas.some((polygon) => !!polygon.regionId);
    if (areas.length > 0) {
      this.mapController.next({
        addGisPolygons: areas.map((polygon) => ({
          id: polygon.id,
          name: polygon.name,
          coordinates: polygon.coordinates,
          wktCoordinates: polygon.wktCoordinates,
          type: 'region' as const,
          fillColor: polygon.fillColor,
          strokeColor: polygon.strokeColor,
          fill: this.districtFillVisible,
          fillOpacity: this.districtFillVisible ? 0.72 : 0,
          weight: 1,
          pane: DISTRICT_PANE,
          popup: polygon.popup,
          tooltip: polygon.tooltip,
          openPopupOnHover: false,
          data: polygon.regionId ? { regionId: polygon.regionId } : undefined,
          onClick: () => {
            if (!polygon.regionId) {
              return;
            }
            this.zone.run(() => this.loadDistrictSeasons(polygon.regionId as string, polygon));
          }
        }))
      });
    }
    this.drawAnomalies();
    this.drawPoints(scene.points);
    for (const layer of wms) {
      this.addRaster(layer.id, (coords) => wmsTileUrl(layer, coords), 0.85);
    }
    this.bounds = scene.bounds;
    if (this.fittedScope !== this.scopeKey()) {
      this.pendingFit = true;
      this.fit();
    }
  }

  /** Anomaly polygons sit in their own pane above the field so they can be toggled on their own. */
  private drawAnomalies(): void {
    if (this.anomalyIds.length > 0) {
      this.mapController.next({ removePolygons: [...this.anomalyIds] });
      this.anomalyIds = [];
    }
    if (!this.anomaliesVisible || this.anomalyPolygons.length === 0) {
      return;
    }
    this.anomalyIds = this.anomalyPolygons.map((polygon) => polygon.id);
    this.mapController.next({
      addGisPolygons: this.anomalyPolygons.map((polygon) => ({
        id: polygon.id,
        name: polygon.name,
        coordinates: polygon.coordinates,
        wktCoordinates: polygon.wktCoordinates,
        type: 'region' as const,
        pane: ANOMALY_PANE,
        fillColor: polygon.fillColor,
        strokeColor: polygon.strokeColor,
        fill: true,
        fillOpacity: 0.55,
        weight: 1.5,
        popup: polygon.popup,
        tooltip: polygon.tooltip,
        openPopupOnHover: true
      }))
    });
  }

  private drawPoints(points: AnalyticsMapPoint[]): void {
    const leaflet = this.leaflet();
    if (!leaflet || !this.mapInstance) {
      return;
    }
    if (!this.pointsLayer) {
      this.pointsLayer = leaflet.layerGroup([], { pane: POINT_PANE });
      this.pointsLayer.addTo(this.mapInstance);
    }
    this.pointsLayer.clearLayers();
    for (const point of points) {
      const marker = leaflet.circleMarker([point.lat, point.lng], {
        pane: POINT_PANE,
        radius: 7,
        color: '#145214',
        weight: 2,
        fillColor: point.fillColor,
        fillOpacity: 0.9
      });
      marker.bindTooltip(point.tooltip, { direction: 'top', sticky: true });
      marker.bindPopup(point.popup);
      marker.addTo(this.pointsLayer);
    }
  }

  private refreshIndexType(): void {
    const inferred = rasterRequest(this.dataset, this.selectedMetric);
    this.indexOptions = inferred?.kind === 'index'
      ? inferred.indexTypes
      : this.rasterModes.includes('index') ? [...INDEX_TYPE_OPTIONS] : [];
    if (!this.indexOptions.includes(this.selectedIndexType)) {
      this.selectedIndexType = this.indexOptions[0] ?? '';
    }
  }

  private syncRaster(): void {
    if (!this.ready) {
      return;
    }
    const request = this.currentRequest();
    const key = [
      this.rasterMode,
      request?.kind ?? '',
      request?.weatherToken ?? '',
      this.selectedIndexType,
      datasetPeriod(this.dataset)?.from ?? '',
      datasetPeriod(this.dataset)?.to ?? '',
      rowIsoDays(this.dataset).join(',')
    ].join('|');
    if (key === this.rasterKey) {
      return;
    }
    this.rasterKey = key;
    this.loadCatalog(request);
  }

  /** Raster to load for the chosen mode; null when the mode is "none" or nothing identifies a layer. */
  private currentRequest(): RasterRequest | null {
    if (this.rasterMode === 'none') {
      return null;
    }
    if (this.rasterMode === 'index') {
      return this.selectedIndexType
        ? { kind: 'index', weatherToken: null, indexTypes: [this.selectedIndexType] }
        : null;
    }
    const inferred = rasterRequest(this.dataset, this.selectedMetric);
    if (inferred?.kind === 'weather') {
      return inferred;
    }
    const token = weatherTokenFor(this.selectedMetric) ?? weatherTokenFor(this.weatherColumns()[0]);
    return token ? { kind: 'weather', weatherToken: token, indexTypes: [] } : null;
  }

  private weatherColumns(): string[] {
    const keys = new Set<string>([
      ...(this.dataset?.columns ?? []),
      ...(this.dataset?.preview_rows ?? []).flatMap((row) => Object.keys(row))
    ]);
    return [...keys].filter((key) => weatherTokenFor(key) !== null);
  }

  private hasVectorGeography(): boolean {
    return (this.maps ?? []).some((map) =>
      (map.kind === 'districts' && (map.districts ?? []).length > 0) ||
      (map.kind === 'features' && (map.features ?? []).length > 0)
    );
  }

  private loadCatalog(request: RasterRequest | null): void {
    this.catalogSub?.unsubscribe();
    this.layerSub?.unsubscribe();
    if (!request) {
      this.dateOptions = [];
      this.selectedDay = '';
      this.rasterNote = '';
      this.clearRaster();
      return;
    }
    const period = datasetPeriod(this.dataset);
    const rows = rowIsoDays(this.dataset);
    // A field answer has no dates of its own: scan the recent catalog and offer the latest passes.
    const openWindow = !period && rows.length === 0;
    const years = openWindow ? recentYears() : yearsCovering(period, rows);
    if (years.length === 0) {
      this.dateOptions = [];
      this.selectedDay = '';
      this.rasterNote = NO_TILES_NOTE;
      this.clearRaster();
      return;
    }
    const calls = request.kind === 'index'
      ? years.map((year) => this.indexManager
        .GetIndexDatesFromTileLayers(request.indexTypes[0] as IndexType, year)
        .pipe(catchError(() => of([] as string[]))))
      : years.map((year) => this.tilesManager
        .GetWeatherLayerDates(year, 'weather', 'era5')
        .pipe(
          map((grouped) => Object.values(grouped ?? {}).flat()),
          catchError(() => of([] as string[]))
        ));
    this.catalogSub = forkJoin(calls).subscribe({
      next: (groups) => {
        const catalog = groups.flat();
        const days = openWindow ? recentCatalogDays(catalog) : catalogDaysInWindow(catalog, period, rows);
        this.dateOptions = days;
        this.selectedDay = days.includes(this.selectedDay) ? this.selectedDay : (defaultCatalogDay(days) ?? '');
        this.rasterNote = days.length === 0 ? NO_TILES_NOTE : '';
        this.applyRaster();
      },
      error: () => {
        this.dateOptions = [];
        this.selectedDay = '';
        this.rasterNote = NO_TILES_NOTE;
        this.clearRaster();
      }
    });
  }

  private applyRaster(): void {
    this.layerSub?.unsubscribe();
    const request = this.currentRequest();
    if (!request || !this.selectedDay) {
      this.clearRaster();
      return;
    }
    if (request.kind === 'index') {
      this.applyIndexRaster(this.selectedIndexType, this.selectedDay);
      return;
    }
    const token = request.weatherToken;
    const compact = this.selectedDay.replace(/-/g, '');
    if (!token) {
      this.clearRaster();
      return;
    }
    this.layerSub = this.tilesManager.loadTileLayers({
      filters: [
        { filterField: 'geoServerName', filterValue: compact, filterOperator: FilterOperator.Contains },
        { filterField: 'geoServerName', filterValue: token, filterOperator: FilterOperator.Contains }
      ]
    }).pipe(catchError(() => of([]))).subscribe((layers: Array<{ geoServerName?: string }> | unknown) => {
      const list = Array.isArray(layers) ? layers as Array<{ geoServerName?: string }> : [];
      const match = list.find((layer) => this.acceptsWeatherLayer(layer.geoServerName, token, compact));
      if (!match?.geoServerName) {
        this.rasterNote = NO_TILES_NOTE;
        this.clearRaster();
        return;
      }
      this.rasterNote = '';
      const name = match.geoServerName;
      this.weatherLayerName = name;
      this.indexRasterOn = false;
      this.replaceDatasetRaster((coords) => weatherTileUrl(environment.api, name, coords), 0.8, 'weather-tile-layer');
      this.cdr.markForCheck();
    });
  }

  /**
   * Index mosaics are drawn by this card, not by the shared IndexManager singleton:
   * the singleton keeps one map and one layer for the whole app and the GIS pages own it.
   */
  private applyIndexRaster(indexType: string, day: string): void {
    this.layerSub = this.indexManager
      .loadAvailableIndices(this.dayToDate(day), indexType)
      .pipe(catchError(() => of([] as Array<{ geoServerName?: string }>)))
      .subscribe((layers: Array<{ geoServerName?: string }>) => {
        const name = layers.find((layer) => layer?.geoServerName)?.geoServerName;
        if (!name) {
          this.rasterNote = NO_TILES_NOTE;
          this.clearRaster();
          return;
        }
        this.rasterNote = '';
        this.weatherLayerName = null;
        this.indexRasterOn = true;
        this.replaceDatasetRaster((coords) => indexTileUrl(environment.api, name, coords), 0.8, 'index-tile-layer');
        this.cdr.markForCheck();
      });
  }

  private replaceDatasetRaster(
    getUrl: (coords: { x: number; y: number; z: number }) => string,
    opacity: number,
    className: string
  ): void {
    this.removeRaster(this.rasterLayerId);
    this.addRaster(this.rasterLayerId, getUrl, opacity, className);
  }

  private acceptsWeatherLayer(name: string | undefined, token: string, compactDay: string): boolean {
    if (!name || !name.includes(token) || !name.includes(compactDay)) {
      return false;
    }
    if (name.includes('Ugm') || name.includes('Ibm')) {
      return false;
    }
    return !isYearlyWeatherLayerName(name) && !isExcludedFromWeatherDataType(name);
  }

  private clearRaster(): void {
    this.weatherLayerName = null;
    this.indexRasterOn = false;
    this.removeRaster(this.rasterLayerId);
  }

  /**
   * Tile layer built the same way as the weather GIS page: lat/lon of the tile centre to the
   * monolith proxy, shared raster options, own pane. Tile errors surface in the toolbar instead
   * of a silently empty map.
   */
  private addRaster(
    id: string,
    getUrl: (coords: { x: number; y: number; z: number }) => string,
    opacity: number,
    className = 'analytics-raster-layer'
  ): void {
    const leaflet = this.leaflet();
    if (!leaflet || !this.mapInstance) {
      return;
    }
    this.removeRaster(id);
    this.ensurePanes();
    const RasterLayer = leaflet.TileLayer.extend({
      getTileUrl: (coords: { x: number; y: number; z: number }) => getUrl(coords)
    });
    const layer = new RasterLayer('', {
      ...OSM_RASTER_TILE_LAYER_OPTIONS,
      opacity,
      pane: RASTER_PANE,
      className
    }) as LeafletTileLayerLike;

    let failed = 0;
    let loaded = 0;
    layer.on('loading', () => {
      failed = 0;
      loaded = 0;
    });
    layer.on('tileload', () => {
      loaded += 1;
    });
    layer.on('tileerror', () => {
      failed += 1;
    });
    layer.on('load', () => {
      const note = failed > 0 && loaded === 0 ? TILES_FAILED_NOTE : '';
      if (note !== this.rasterNote && (note || this.rasterNote === TILES_FAILED_NOTE)) {
        this.zone.run(() => {
          this.rasterNote = note;
          this.cdr.markForCheck();
        });
      }
    });

    layer.addTo(this.mapInstance);
    this.rasterLayers.set(id, layer);
  }

  private removeRaster(id: string): void {
    const layer = this.rasterLayers.get(id);
    if (!layer) {
      return;
    }
    this.rasterLayers.delete(id);
    try {
      layer.off?.();
      if (this.mapInstance?.hasLayer(layer)) {
        this.mapInstance.removeLayer(layer);
      }
    } catch {
      /* map already destroyed */
    }
  }

  private dayToDate(day: string): Date {
    const [year, month, date] = day.split('-').map((part) => Number(part));
    return new Date(year, month - 1, date);
  }

  private isScrapedRasterName(name: string): boolean {
    return isIndexLayerName(name) || weatherLayerNames(name).length > 0;
  }

  private viewKey(): string {
    const source = this.selectedMetric === DENSITY_METRIC ? this.densitySource ?? '' : '';
    return `${sceneKey(this.maps, this.dataset, this.selectedMetric)}|${source}`;
  }

  /** Identity of the answer, without the metric: a manual raster choice survives metric changes but not a new answer. */
  private scopeKey(): string {
    return sceneKey(this.maps, this.dataset, null);
  }

  private ensurePanes(): void {
    const map = this.mapInstance;
    if (!map?.createPane) {
      return;
    }
    if (!map.getPane(RASTER_PANE)) {
      const pane = map.createPane(RASTER_PANE);
      pane.style.zIndex = RASTER_PANE_Z_INDEX;
      pane.style.pointerEvents = 'none';
    }
    if (!map.getPane(ANOMALY_PANE)) {
      const pane = map.createPane(ANOMALY_PANE);
      pane.style.zIndex = ANOMALY_PANE_Z_INDEX;
    }
    if (!map.getPane(POINT_PANE)) {
      const pane = map.createPane(POINT_PANE);
      pane.style.zIndex = POINT_PANE_Z_INDEX;
    }
    if (!map.getPane(DISTRICT_PANE)) {
      const pane = map.createPane(DISTRICT_PANE);
      pane.style.zIndex = DISTRICT_PANE_Z_INDEX;
      pane.style.pointerEvents = 'auto';
    }
    if (!map.getPane(PLAN_PANE)) {
      const pane = map.createPane(PLAN_PANE);
      pane.style.zIndex = PLAN_PANE_Z_INDEX;
    }
    if (!map.getPane(FACT_PANE)) {
      const pane = map.createPane(FACT_PANE);
      pane.style.zIndex = FACT_PANE_Z_INDEX;
    }
  }

  private addZoomControl(map: LeafletMapLike): void {
    if (this.zoomControl) {
      return;
    }
    const leaflet = (window as unknown as {
      L?: { control?: { zoom?: (options: unknown) => {
        addTo: (target: unknown) => void;
        remove?: () => void;
        _zoomInButton?: HTMLElement;
        _zoomOutButton?: HTMLElement;
      } } };
    }).L;
    if (!leaflet?.control?.zoom) {
      return;
    }
    const titles = () => ({
      zoomInTitle: this.translate.instant('Zoom In'),
      zoomOutTitle: this.translate.instant('Zoom Out')
    });
    const control = leaflet.control.zoom({ position: 'topright', ...titles() });
    control.addTo(map);
    this.zoomControl = control;
    const applyTitles = () => {
      const { zoomInTitle, zoomOutTitle } = titles();
      if (control._zoomInButton) {
        control._zoomInButton.title = zoomInTitle;
        control._zoomInButton.setAttribute('aria-label', zoomInTitle);
      }
      if (control._zoomOutButton) {
        control._zoomOutButton.title = zoomOutTitle;
        control._zoomOutButton.setAttribute('aria-label', zoomOutTitle);
      }
    };
    applyTitles();
    this.langSub?.unsubscribe();
    this.langSub = this.translate.onLangChange.subscribe(() => applyTitles());
  }

  private bindMapEvents(): void {
    const zoom$ = this.osm?.mapZoomChange$;
    if (this.mapEventsBound || !this.mapInstance) {
      return;
    }
    this.mapEventsBound = true;
    this.mapEventsSub?.unsubscribe();
    this.mapEventsSub = new Subscription();
    if (zoom$) {
      this.mapEventsSub.add(zoom$.subscribe((zoom) => {
        this.zoomLabel = String(Math.round(zoom));
      }));
    }
    const map = this.mapInstance as LeafletMapLike & {
      on?: (event: string, handler: () => void) => void;
    };
    map.on?.('dragend', () => {
      this.userAdjustedView = true;
    });
    map.on?.('zoomend', () => {
      if (this.fitting) {
        this.fitting = false;
        return;
      }
      this.userAdjustedView = true;
    });
  }

  private loadDistrictSeasons(regionId: string, polygon: AnalyticsMapPolygon): void {
    const points = polygon.wktCoordinates.flatMap((rings) => rings.flat());
    const box = latLngBoundsBox(points);
    if (!box || !this.mapInstance) {
      return;
    }
    this.seasonSub?.unsubscribe();
    this.seasonsLoading = true;
    this.seasonNote = '';
    const cropId = datasetCropId(this.dataset);
    const lang = this.translate.currentLang;
    const request = (planned: boolean, restrictToRegion: boolean) => this.seasonService.cropMonitoringSeasonGetSeasonsByCoordinatesPost(lang, {
      west: box.west,
      east: box.east,
      south: box.south,
      north: box.north,
      query: {
        filters: (restrictToRegion
          ? seasonLayerFilters(planned, regionId, cropId)
          : seasonLayerFilters(planned, regionId, cropId).filter((filter) => filter.filterField !== 'regionId')) as FieldFilter[]
      }
    }).pipe(catchError(() => of([] as SeasonMapDto[])));
    const loadPair = (restrictToRegion: boolean) => forkJoin({
      plan: request(true, restrictToRegion),
      fact: request(false, restrictToRegion)
    });
    this.seasonSub = this.cropCache.getCrops().pipe(
      catchError(() => of([])),
      switchMap(() => loadPair(true)),
      switchMap((first) => (first.plan.length === 0 && first.fact.length === 0) ? loadPair(false) : of(first))
    ).subscribe({
      next: ({ plan, fact }) => {
        this.planSeasons = this.toSeasons(plan, 'plan');
        this.factSeasons = this.toSeasons(fact, 'fact');
        this.seasonsLoaded = true;
        this.seasonsLoading = false;
        this.seasonNote = this.planSeasons.length === 0 && this.factSeasons.length === 0
          ? `В районе «${polygon.name}» нет плановых и фактических посевов.`
          : '';
        this.drawSeasonLayer('plan');
        this.drawSeasonLayer('fact');
        this.cdr.markForCheck();
      },
      error: () => {
        this.seasonsLoading = false;
        this.seasonNote = 'Не удалось загрузить посевы района.';
        this.cdr.markForCheck();
      }
    });
  }

  private toSeasons(seasons: SeasonMapDto[], kind: 'plan' | 'fact'): DrawnSeason[] {
    const drawn: DrawnSeason[] = [];
    seasons.forEach((season, index) => {
      const rings = wktToLeafletPolygons(season.polygonJson);
      if (rings.length === 0) {
        return;
      }
      const planned = kind === 'plan';
      const fill = this.seasonFill(season, planned);
      const name = season.fieldName || season.title || (planned ? 'Плановый посев' : 'Фактический посев');
      const crop = planned ? season.plannedCropName || season.cropName : season.cropName || season.plannedCropName;
      const parts = [`<strong>${escapeHtml(name)}</strong>`, planned ? 'Плановый посев' : 'Фактический посев'];
      if (crop) {
        parts.push(escapeHtml(crop));
      }
      if (typeof season.fieldArea === 'number' && Number.isFinite(season.fieldArea)) {
        parts.push(`${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(season.fieldArea)} га`);
      }
      drawn.push({
        id: `analytics-${kind}-${season.id ?? index}`,
        name,
        coordinates: rings[0],
        wktCoordinates: rings.map((ring) => [ring]),
        popup: parts.join('<br>'),
        fill
      });
    });
    return drawn;
  }

  private drawSeasonLayer(kind: 'plan' | 'fact'): void {
    const items = kind === 'plan' ? this.planSeasons : this.factSeasons;
    const visible = kind === 'plan' ? this.planSeasonsVisible : this.factSeasonsVisible;
    const previous = kind === 'plan' ? this.planSeasonIds : this.factSeasonIds;
    if (previous.length > 0) {
      this.mapController.next({ removePolygons: [...previous] });
    }
    if (!visible || items.length === 0) {
      if (kind === 'plan') {
        this.planSeasonIds = [];
      } else {
        this.factSeasonIds = [];
      }
      return;
    }
    const pane = kind === 'plan' ? PLAN_PANE : FACT_PANE;
    const ids = items.map((item) => item.id);
    if (kind === 'plan') {
      this.planSeasonIds = ids;
    } else {
      this.factSeasonIds = ids;
    }
    this.ensurePanes();
    this.mapController.next({
      addGisPolygons: items.map((item) => ({
        id: item.id,
        name: item.name,
        coordinates: item.coordinates,
        wktCoordinates: item.wktCoordinates,
        type: 'season' as const,
        pane,
        fillColor: item.fill,
        strokeColor: item.fill,
        fill: true,
        fillOpacity: 0.72,
        weight: 1.5,
        popup: item.popup,
        tooltip: item.popup,
        openPopupOnHover: true
      }))
    });
  }

  private clearSeasonLayers(): void {
    this.seasonSub?.unsubscribe();
    this.seasonSub = null;
    const ids = [...this.planSeasonIds, ...this.factSeasonIds];
    if (ids.length > 0) {
      this.mapController.next({ removePolygons: ids });
    }
    this.planSeasonIds = [];
    this.factSeasonIds = [];
    this.planSeasons = [];
    this.factSeasons = [];
    this.seasonsLoaded = false;
    this.seasonsLoading = false;
    this.seasonNote = '';
  }

  private hasSize(): boolean {
    try {
      const size = this.mapInstance?.getSize();
      return !!size && size.x > 0 && size.y > 0;
    } catch {
      return false;
    }
  }

  /**
   * Fits the drawn geometry. While the card sits in a hidden tab the map has no size and Leaflet
   * would clamp the zoom to the minimum, so the fit is postponed until the canvas is visible.
   */
  private fit(): void {
    if (this.userAdjustedView) {
      this.pendingFit = false;
      return;
    }
    const leaflet = this.leaflet();
    if (!this.mapInstance || !leaflet || this.bounds.length < 1) {
      this.pendingFit = false;
      return;
    }
    if (!this.hasSize()) {
      this.pendingFit = true;
      return;
    }
    this.pendingFit = false;
    this.fittedScope = this.scopeKey();
    this.fitting = true;
    const latLngBounds = leaflet.latLngBounds(this.bounds);
    this.mapInstance.fitBounds(latLngBounds, { padding: [24, 24], maxZoom: FIT_MAX_ZOOM });
    setTimeout(() => {
      this.fitting = false;
    }, 400);
  }

  /** Same crop palette as the GIS season polygons. The stroke uses the fill, not a white outline. */
  private seasonFill(season: SeasonMapDto, planned: boolean): string {
    const cropId = planned
      ? season.plannedCropId || season.cropId
      : season.cropId || season.plannedCropId;
    const fallback = planned ? PLAN_SEASON_COLOR : FACT_SEASON_COLOR;
    if (!cropId) {
      return fallback;
    }
    return this.cropCache.getCropColorById(String(cropId), fallback);
  }

  private leaflet(): LeafletLike | null {
    return (window as unknown as { L?: LeafletLike }).L ?? null;
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
